import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { NAV_ITEMS } from '../constants';
import { Logo } from './ui/Logo';
import { CookieBanner } from './CookieBanner';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen flex flex-col bg-transparent text-white font-body selection:bg-astral-purple selection:text-white">
      <nav 
        className={`fixed w-full z-50 transition-all duration-700 border-b ${
          isScrolled || mobileMenuOpen ? 'bg-astral-void/80 backdrop-blur-xl border-white/5 py-4' : 'bg-transparent border-transparent py-8'
        }`}
      >
        <div className="container mx-auto px-6 flex justify-between items-center relative">
          <Link to="/" className="hidden md:flex cursor-pointer group hover:opacity-90 transition-opacity">
            <Logo className="w-10 h-10 drop-shadow-[0_0_10px_rgba(96,37,192,0.5)]" withText={true} />
          </Link>
          <Link to="/" className="md:hidden cursor-pointer group hover:opacity-90 transition-opacity">
            <Logo className="w-8 h-8" withText={false} />
          </Link>
          <Link to="/" className="absolute left-1/2 -translate-x-1/2 md:hidden cursor-pointer group hover:opacity-90 transition-opacity">
            <Logo withIcon={false} />
          </Link>
          
          <div className="hidden md:flex gap-8 items-center">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `text-xs tracking-[0.2em] uppercase font-bold transition-all duration-300 hover:text-astral-purple relative after:content-[''] after:absolute after:-bottom-2 after:left-0 after:h-[2px] after:bg-astral-purple after:transition-all after:duration-300 hover:after:w-full ${
                    isActive ? 'text-astral-purple after:w-full drop-shadow-[0_0_8px_rgba(96,37,192,0.8)]' : 'text-slate-300'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link 
                to="/booking"
                className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-7 py-2.5 text-xs font-bold tracking-widest uppercase hover:bg-astral-purple hover:border-astral-purple transition-all duration-300 shadow-[0_0_20px_rgba(0,0,0,0.5)] hover:shadow-[0_0_20px_rgba(96,37,192,0.5)]"
            >
              Book Now
            </Link>
          </div>

          <button 
            className="md:hidden text-white hover:text-astral-purple transition-colors z-[60]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        <div
          className={`
            md:hidden fixed inset-0 top-0 left-0 w-full bg-astral-black/95 backdrop-blur-2xl 
            flex flex-col items-center justify-between h-screen z-50 py-20 px-12
            transition-opacity duration-300 ease-in-out
            ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
          `}
        >
          <Link to="/" onClick={closeMobileMenu} className="cursor-pointer">
            <Logo className="w-10 h-10" withText={true} />
          </Link>
          
           <div className="flex flex-col gap-8 items-center">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  `text-2xl tracking-[0.2em] uppercase font-display font-bold ${
                    isActive ? 'text-astral-purple drop-shadow-[0_0_10px_rgba(96,37,192,0.8)]' : 'text-white'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
           </div>
          
           <div className="flex flex-col items-center w-full max-w-xs gap-8">
            <div className="w-full max-w-[200px] border-t border-white/10"></div>
            <Link 
              to="/booking"
              onClick={closeMobileMenu}
              className="w-full text-center bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-astral-purple hover:border-astral-purple transition-all duration-300"
            >
                Book Now
            </Link>
           </div>
        </div>
      </nav>

      <main className="flex-grow pt-24 relative z-10 w-full overflow-x-hidden">
        {children}
      </main>

      <footer className="bg-black/60 backdrop-blur-lg border-t border-white/5 py-20 mt-20 relative z-10">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 md:gap-16 mb-16">
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="mb-8 inline-block">
                <Logo className="w-10 h-10" withText={true} />
              </Link>
              <p className="text-slate-300 max-w-md leading-relaxed text-lg font-light">
                We are the architects of the invisible. <br/>
                Curating frequencies that bridge the gap between organic rhythm and the astral plane.
              </p>
            </div>
            <div>
              <h4 className="text-white font-display uppercase tracking-widest mb-8 text-xs font-bold text-astral-purple">Explore</h4>
              <ul className="space-y-4 text-slate-300 text-sm tracking-wide">
                <li><Link to="/artists" className="hover:text-astral-purple hover:pl-2 transition-all">Roster</Link></li>
                <li><Link to="/releases" className="hover:text-astral-purple hover:pl-2 transition-all">Discography</Link></li>
                <li><Link to="/services" className="hover:text-astral-purple hover:pl-2 transition-all">Agency</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-display uppercase tracking-widest mb-8 text-xs font-bold text-astral-purple">Connect</h4>
              <ul className="space-y-4 text-slate-300 text-sm tracking-wide">
                 <li>
                  <a href="https://www.instagram.com/astralvibemusic?igsh=MWMxbWhmcTVsZ2l4Zw%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="hover:text-astral-purple hover:pl-2 transition-all block">Instagram</a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-slate-500 uppercase tracking-widest gap-4">
            <p className="order-1">&copy; 2026 AstralVibe. All rights reserved.</p>
            <div className="flex gap-4 md:gap-6 flex-wrap justify-center order-3 md:order-2">
                <Link to="/legal-notice" className="hover:text-white transition-colors">Legal Notice</Link>
                <Link to="/copyright-notice" className="hover:text-white transition-colors">Copyright</Link>
                <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
                <Link to="/cookie-policy" className="hover:text-white transition-colors">Cookies</Link>
                <Link to="/admin/login" className="hover:text-white transition-colors">Admin</Link>
            </div>
            <p className="opacity-50 order-2 md:order-3">Designed by Astral AI</p>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
};